import React, { useState, useEffect } from 'react';
import {
  Typography,
  Stack,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import contactService from '../../../services/contactService';
import { Contact } from '../../../types/crm/types/contact';

interface ContactEditPageProps {
  id: number;
  contact: Contact;
  onClose: () => void;
  onUpdate?: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

const ContactEditPage: React.FC<ContactEditPageProps> = ({ 
  id, 
  contact, 
  onClose, 
  onUpdate,
  onSuccess,
  onError
}) => {
  const theme = useTheme();
  const [form, setForm] = useState<Contact>(contact);
  const [errors, setErrors] = useState<Partial<Record<keyof Contact, string>>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(contact);
  }, [contact]);

  const handleChange = (field: keyof Contact, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof Contact, string>> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{8,15}$/;

    if (!form.fullName.trim()) newErrors.fullName = 'El nombre completo es requerido';
    if (!form.phone.trim()) {
      newErrors.phone = 'El teléfono es requerido';
    } else if (!phoneRegex.test(form.phone)) {
      newErrors.phone = 'El teléfono debe tener entre 8 y 15 dígitos';
    }

    if (!form.type.trim()) newErrors.type = 'El tipo de contacto es requerido';
    if (!form.email?.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!emailRegex.test(form.email)) {
      newErrors.email = 'El correo electrónico no es válido';
    }

    if (!form.address?.trim()) newErrors.address = 'La dirección es requerida';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const result = await contactService.updateContact(id, form);
      
      if (result.success) {
        onSuccess?.('Contacto actualizado con éxito');
        onUpdate?.();
        onClose();
      } else {
        onError?.(result.message || 'Error al actualizar el contacto');
      }
    } catch (error) {
      console.error('Error al actualizar el contacto:', error);
      onError?.('Ocurrió un error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 3,
          backdropFilter: 'blur(8px)',
          boxShadow: theme.shadows[5],
          p: 3,
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Editar Contacto
          </Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <TextField
            required
            label="Nombre completo"
            value={form.fullName}
            onChange={(e) => handleChange('fullName', e.target.value)}
            fullWidth
            error={!!errors.fullName}
            helperText={errors.fullName}
          />
          <TextField
            required
            label="Teléfono"
            value={form.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            fullWidth
            error={!!errors.phone}
            helperText={errors.phone}
          />
          <TextField
            required
            label="Tipo de contacto"
            value={form.type}
            onChange={(e) => handleChange('type', e.target.value)}
            fullWidth
            error={!!errors.type}
            helperText={errors.type}
          />
          <TextField
            required
            label="Correo electrónico"
            value={form.email}
            onChange={(e) => handleChange('email', e.target.value)}
            fullWidth
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            required
            label="Dirección"
            value={form.address}
            onChange={(e) => handleChange('address', e.target.value)}
            fullWidth
            error={!!errors.address}
            helperText={errors.address}
          />

          <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
            <Button variant="outlined" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ContactEditPage;